/**
 * @fileoverview Element-level selection in a rendered report (MOR-924).
 *
 * Backs the report's "select an element" mode: the user points at a specific
 * paragraph, table, row or chart instead of tagging a whole section, and that
 * element becomes the target the assistant edits. These helpers resolve a DOM
 * event target to the block the user meant, describe it, and package it as the
 * `ReportSelection` sent to the section-writer.
 */

/** A part of the report the user picked out as the target for an edit. */
export interface ReportSelection {
  /** Heading of the section containing the element (null if above the first). */
  sectionTitle: string | null;
  /** Human-readable kind, e.g. "Paragraph" / "Table row". */
  label: string;
  /** Position among the section's selectable blocks — disambiguates repeats. */
  blockIndex: number;
  /** Short plain-text preview, shown on the composer chip. */
  excerpt: string;
  /** The element's HTML — the writer's exact edit target. */
  html: string;
}

/** Block elements a user may target. Order is irrelevant; `closest` decides. */
const SELECTABLE_SELECTOR =
  "p, table, tr, li, h1, h2, h3, h4, blockquote, pre, div[data-morgan-chart]";

const HEADING_SELECTOR = "h1, h2, h3";

const LABELS: Record<string, string> = {
  P: "Paragraph",
  TABLE: "Table",
  TR: "Table row",
  LI: "List item",
  H1: "Heading",
  H2: "Heading",
  H3: "Heading",
  H4: "Heading",
  BLOCKQUOTE: "Quote",
  PRE: "Code block",
};

/** Cap on the HTML handed to the model for one selected element. */
export const MAX_SELECTION_HTML_CHARS = 8000;
const MAX_EXCERPT_CHARS = 120;

/**
 * The element the user meant when pointing at `target`: the nearest selectable
 * ancestor, resolved to the whole row when the point lands inside a table cell
 * (a lone paragraph inside a cell is rarely the intended edit target).
 * Returns null when the point is outside `root` or hits no block.
 */
export function selectableFrom(
  target: Element | null,
  root: Element,
): HTMLElement | null {
  if (!target || !root.contains(target)) return null;
  const el = target.closest<HTMLElement>(SELECTABLE_SELECTOR);
  if (!el || !root.contains(el)) return null;
  const cell = el.closest<HTMLElement>("td, th");
  if (cell && el !== cell) {
    const row = cell.closest<HTMLElement>("tr");
    if (row && root.contains(row)) return row;
  }
  return el;
}

/** Human-readable kind for a selected element. */
export function describeElement(el: Element): string {
  if (el.hasAttribute("data-morgan-chart")) return "Chart";
  return LABELS[el.tagName] ?? "Block";
}

/** Collapse an element's text into a short single-line preview. */
export function excerptOf(
  el: Element,
  max: number = MAX_EXCERPT_CHARS,
): string {
  const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

/**
 * Locate an element in the report: which section it belongs to (the nearest
 * heading before it in document order) and its position among that section's
 * selectable blocks. One document-order pass — a per-element search would
 * both be quadratic and, because ancestors precede their children, easy to
 * get wrong.
 */
export function locateElement(
  el: Element,
  root: Element,
): { sectionTitle: string | null; blockIndex: number } {
  // A selected heading names its own section.
  if (el.matches(HEADING_SELECTOR)) {
    return { sectionTitle: el.textContent?.trim() || null, blockIndex: 0 };
  }
  let sectionTitle: string | null = null;
  let blockIndex = 0;
  for (const node of Array.from(
    root.querySelectorAll<HTMLElement>(SELECTABLE_SELECTOR),
  )) {
    if (node === el) break;
    if (node.matches(HEADING_SELECTOR)) {
      sectionTitle = node.textContent?.trim() || sectionTitle;
      blockIndex = 0;
    } else {
      blockIndex++;
    }
  }
  return { sectionTitle, blockIndex };
}

/** The section heading an element sits under. */
export function sectionTitleFor(el: Element, root: Element): string | null {
  return locateElement(el, root).sectionTitle;
}

/** Package a picked element as the selection sent with the chat message. */
export function buildSelection(
  el: HTMLElement,
  root: Element,
): ReportSelection {
  const { sectionTitle, blockIndex } = locateElement(el, root);
  const html = el.outerHTML ?? "";
  return {
    sectionTitle,
    label: describeElement(el),
    blockIndex,
    excerpt: excerptOf(el),
    html:
      html.length > MAX_SELECTION_HTML_CHARS
        ? html.slice(0, MAX_SELECTION_HTML_CHARS)
        : html,
  };
}

/** One-line description of a selection, for the composer chip. */
export function selectionChipLabel(selection: ReportSelection): string {
  const where = selection.sectionTitle ? `${selection.sectionTitle} · ` : "";
  return `${where}${selection.label}`;
}

/**
 * The selection as the Convex mutation takes it. `sectionTitle` is optional
 * over the wire (Convex has no `null` for an optional field), so an element
 * sitting above the first heading sends the key absent rather than null.
 */
export function toWireSelection(selection: ReportSelection): {
  sectionTitle?: string;
  label: string;
  blockIndex?: number;
  excerpt: string;
  html: string;
} {
  return {
    sectionTitle: selection.sectionTitle ?? undefined,
    label: selection.label,
    blockIndex: selection.blockIndex,
    excerpt: selection.excerpt,
    html: selection.html,
  };
}
